/**
 * BilanFiscalBreakdown — Volet pédagogique "Décomposition fiscale" (17/06/2026).
 *
 * Suite au retour utilisateur sur le calcul des capitaux propres : les
 * provisions et réserves (TVA, IS, fiscale) étaient soustraites du cash
 * mais pas réinjectées à l'actif → équité sous-estimée. Ce volet :
 *   1. Explique chaque ligne du bilan
 *   2. Met en regard ACTIF vs PASSIF
 *   3. Précise quelles provisions compensent quelles dettes fiscales
 *
 * Accessible depuis la page Stratégie, cliquable pour développer/réduire.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Calculator, TrendingUp, TrendingDown } from 'lucide-react';
import { formatMoney } from '../../utils/format';

function Line({ label, value, hint, positive, indent = 0, testId }) {
  return (
    <div
      className={`flex items-start justify-between gap-3 text-xs ${
        indent > 0 ? 'pl-' + indent * 3 : ''
      }`}
      data-testid={testId || `bilan-line-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-slate-300">{label}</p>
        {hint && <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{hint}</p>}
      </div>
      <p
        className={`font-mono tabular-nums whitespace-nowrap ${
          positive === false ? 'text-danger' : positive === true ? 'text-money' : 'text-foreground'
        }`}
      >
        {value < 0 ? '−' : positive === true ? '+' : ''}{formatMoney(Math.abs(value))}
      </p>
    </div>
  );
}

export default function BilanFiscalBreakdown({ balanceSheet }) {
  const [open, setOpen] = useState(false);
  if (!balanceSheet) return null;
  const { actif, passif, equity, solvencyPct } = balanceSheet;
  const hasProvisions = (actif.tvaProvisioned > 0) || (actif.isProvisioned > 0) || (actif.fiscalReserveTotal > 0);

  return (
    <div
      className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden"
      data-testid="bilan-fiscal-breakdown"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 p-3 hover:bg-ink-700/30 transition-colors"
        data-testid="bilan-fiscal-toggle"
      >
        <div className="flex items-center gap-2 text-left">
          <Calculator className="w-4 h-4 text-accent" />
          <div>
            <p className="text-sm font-bold text-foreground">Décomposition fiscale du bilan</p>
            <p className="text-[10px] text-slate-500">
              Comment vos provisions et réserves impactent vos capitaux propres
            </p>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-4">
          {/* ACTIF */}
          <div className="bg-money/5 border border-money/30 rounded-md p-3 space-y-1.5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-mono uppercase tracking-wider text-money flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Actif (ce que vous possédez)
              </p>
              <p className="font-mono font-bold text-money tabular-nums">{formatMoney(actif.total)}</p>
            </div>
            <Line label="Trésorerie courante" value={actif.cash} hint="Compte courant — net des provisions transférées." />
            <Line label="Placements actifs" value={actif.placements} hint="Livret entreprise, fonds court terme." />
            <Line label="Valeur de revente flotte" value={actif.fleetGross} hint="Camions possédés (hors leasing) — prix de revente actuel." />

            {hasProvisions && (
              <>
                <div className="pt-2 mt-2 border-t border-money/20">
                  <p className="text-[10px] font-mono uppercase text-money/80 mb-1.5">
                    Provisions fiscales (cash immobilisé temporairement)
                  </p>
                  {actif.tvaProvisioned > 0 && (
                    <Line
                      label="Provision TVA collectée"
                      value={actif.tvaProvisioned}
                      hint="Cash mis de côté pour la prochaine échéance TVA. Compense la dette TVA au passif."
                      testId="bilan-line-tva-provisioned"
                    />
                  )}
                  {actif.isProvisioned > 0 && (
                    <Line
                      label="Acompte IS payé"
                      value={actif.isProvisioned}
                      hint="Acomptes versés à l'État sur l'impôt sur les sociétés. Compense la provision IS au passif."
                      testId="bilan-line-is-provisioned"
                    />
                  )}
                  {actif.fiscalReserveTotal > 0 && (
                    <Line
                      label="Réserve fiscale (+ intérêts)"
                      value={actif.fiscalReserveTotal}
                      hint="Placement à 3 % servant de tampon fiscal. Inclut les intérêts accrus."
                      testId="bilan-line-fiscal-reserve"
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* PASSIF */}
          <div className="bg-danger/5 border border-danger/30 rounded-md p-3 space-y-1.5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-mono uppercase tracking-wider text-danger flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> Passif (ce que vous devez)
              </p>
              <p className="font-mono font-bold text-danger tabular-nums">{formatMoney(passif.total)}</p>
            </div>
            <Line label="Crédits bancaires (capital restant dû)" value={passif.credits} />
            <Line label="Engagement leasing (mensualités + valeur résiduelle)" value={passif.leaseEngagement} />
            <Line label="Dette TVA exigible" value={passif.tvaPending}
              hint="TVA collectée − TVA déductible non encore reversée. Compensée par votre Provision TVA si activée." />
            <Line label="IS dû sur le résultat à date" value={passif.isProvision}
              hint={`Barème PME sur le bénéfice réellement engrangé cette année, net des acomptes déjà versés${(passif.isPaidYtd || 0) > 0 ? ` (${formatMoney(passif.isPaidYtd)} payés)` : ''}.`} />
            {(passif.isProjected || 0) > (passif.isProvision || 0) && (
              <div className="flex items-start justify-between gap-3 text-xs opacity-70" data-testid="bilan-line-is-projected">
                <div className="flex-1 min-w-0">
                  <p className="text-slate-400 italic">IS projeté fin d&apos;exercice (indicatif)</p>
                  <p className="text-[10px] text-slate-500 leading-snug mt-0.5">
                    Extrapolation de votre rythme actuel sur 12 mois. Purement informatif — non compté dans le passif ni dans les capitaux propres.
                  </p>
                </div>
                <p className="font-mono tabular-nums whitespace-nowrap text-slate-400 italic">
                  ≈ {formatMoney(passif.isProjected)}
                </p>
              </div>
            )}
          </div>

          {/* ÉQUITÉ */}
          <div className="bg-accent/5 border border-accent/30 rounded-md p-3 space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono uppercase tracking-wider text-accent">
                Capitaux propres
              </p>
              <p className={`font-mono font-bold text-base tabular-nums ${
                equity < 0 ? 'text-danger' : 'text-accent'
              }`}>
                {equity >= 0 ? '+' : ''}{formatMoney(equity)}
              </p>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              <span className="font-mono">CP = Actif ({formatMoney(actif.total)}) − Passif ({formatMoney(passif.total)})</span><br />
              Ratio de solvabilité : <strong className="text-foreground">{solvencyPct} %</strong>{' '}
              ({solvencyPct >= 30 ? 'sain' : solvencyPct >= 15 ? 'à surveiller' : 'critique'})
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
